import {
	faAlignCenter,
	faAlignLeft,
	faAlignRight,
	faHeader,
	faObjectsAlignBottom,
	faObjectsAlignCenterVertical,
	faObjectsAlignTop,
	faPlus,
	faRulerHorizontal,
	faSort,
	faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { Icon } from '@supply-mover-2-0/web-ui';
import React, { useState } from 'react';
import { Text } from '../../../../../components/base/Text';
import { Card } from '../../../../../components/display/Card';
import { Input } from '../../../../../components/forms/Input';
import { useThemeColors } from '../../../../../providers/ThemeProvider';
import { cn } from '../../../../../utils/cn';
import { DraggableList, DragHandle } from '../../../shared/DraggableList';
import type {
	ColumnConfiguration,
	ColumnWidthConfig,
	HeaderConfiguration,
	ListBuilderConfiguration,
	ListConfiguration,
} from '../../../types';
import { SortFieldsList } from '../../sorting/SortFieldsList';

interface ColumnDetailsTabProps {
	selectedColumn: ColumnConfiguration | null;
	selectedColumnIndex: number;
	listConfig: ListConfiguration;
	configuration?: ListBuilderConfiguration;
	availableFields: any[];
	onUpdateColumn: (updates: Partial<ColumnConfiguration>) => void;
	onUpdateList: (updates: Partial<ListConfiguration>) => void;
}

export function ColumnDetailsTab({
	selectedColumn,
	selectedColumnIndex,
	listConfig,
	configuration,
	availableFields,
	onUpdateColumn,
	onUpdateList,
}: ColumnDetailsTabProps) {
	const colors = useThemeColors();

	// State for delete confirmations
	const [showConfirmDeleteSort, setShowConfirmDeleteSort] = useState<
		number | null
	>(null);

	if (!selectedColumn) {
		return (
			<div className="flex-1 flex items-center justify-center p-6">
				<p className={`text-base ${colors.classes.textSecondary} text-center`}>
					Select a column to configure
				</p>
			</div>
		);
	}

	// Get available fields from the Objects tab configuration
	const getAvailableFieldsFromObjects = () => {
		if (!configuration?.objects || configuration.objects.length === 0) {
			return [];
		}

		const fields: Array<{
			fieldName: string;
			fieldLabel: string;
			objectTable: string;
			objectLabel: string;
		}> = [];

		configuration.objects.forEach((obj) => {
			obj.selectedFields.forEach((field) => {
				fields.push({
					fieldName: field.fieldName,
					fieldLabel: field.fieldLabel,
					objectTable: obj.objectTable,
					objectLabel: obj.objectLabel,
				});
			});
		});

		return fields;
	};

	const availableFieldsFromObjects = getAvailableFieldsFromObjects();

	// Headers functions
	const addHeader = () => {
		const newHeader: HeaderConfiguration = {
			id: `header_${Date.now()}`,
			text: 'New Header',
		};

		const updatedHeaders = [...selectedColumn.headers, newHeader];
		onUpdateColumn({
			headers: updatedHeaders,
		});
	};

	const updateHeader = (headerIndex: number, text: string) => {
		const updatedHeaders = [...selectedColumn.headers];
		updatedHeaders[headerIndex] = { ...updatedHeaders[headerIndex], text };

		onUpdateColumn({
			headers: updatedHeaders,
		});
	};

	const deleteHeader = (headerIndex: number) => {
		if (selectedColumn.headers.length <= 1) return; // Keep at least one header

		const updatedHeaders = selectedColumn.headers.filter(
			(_, i) => i !== headerIndex,
		);
		onUpdateColumn({
			headers: updatedHeaders,
		});
	};

	const reorderHeaders = (reorderedHeaders: HeaderConfiguration[]) => {
		onUpdateColumn({
			headers: reorderedHeaders,
		});
	};

	const renderHeadersSection = () => (
		<Card className="mb-4">
			<div className="p-4">
				<div className="flex justify-between items-center mb-4">
					<div className="flex items-center">
						<Icon
							icon={faHeader}
							size="sm"
							className={`mr-2 ${colors.classes.textSecondary}`}
						/>
						<h3
							className={`text-base font-semibold ${colors.classes.textPrimary}`}
						>
							Headers ({selectedColumn.headers.length})
						</h3>
					</div>
					<button
						onClick={addHeader}
						className="flex items-center px-2.5 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded text-xs font-medium"
					>
						<Icon icon={faPlus} size="xs" className="mr-1" />
						Add
					</button>
				</div>

				<DraggableList
					data={selectedColumn.headers}
					keyExtractor={(header) => header.id}
					dragDataExtractor={(header) => header.id}
					listId="headers-list"
					allowInternalReordering={true}
					allowPositionalDrops={true}
					acceptDropFrom={[]}
					onDrop={(dragData, targetIndex) => {
						// Reordering handled by onReorder
					}}
					renderItem={(header, index, isDragging) => (
						<div
							className={`
              flex items-center p-3 ${colors.classes.bgPrimary} border ${colors.classes.borderDefault} rounded-lg mb-2
              ${isDragging ? 'opacity-50' : ''}
            `}
						>
							<DragHandle className="mr-1" />

							<input
								type="text"
								value={header.text}
								onChange={(e) => updateHeader(index, e.target.value)}
								className={`flex-1 px-2 py-1.5 border ${colors.classes.borderDefault} rounded text-sm ${colors.classes.bgPrimary} ${colors.classes.textPrimary}`}
							/>

							{selectedColumn.headers.length > 1 && (
								<button
									onClick={() => deleteHeader(index)}
									className={`ml-2 p-1 rounded ${colors.classes.bgPrimaryHover}`}
								>
									<Icon
										icon={faTrash}
										size="sm"
										className={colors.classes.textSecondary}
									/>
								</button>
							)}
						</div>
					)}
					onReorder={reorderHeaders}
				/>
			</div>
		</Card>
	);

	const renderAlignmentSection = () => {
		const horizontalAlignmentOptions = [
			{ value: 'left' as const, label: 'Left', icon: faAlignLeft },
			{ value: 'center' as const, label: 'Center', icon: faAlignCenter },
			{ value: 'right' as const, label: 'Right', icon: faAlignRight },
		];

		const verticalAlignmentOptions = [
			{ value: 'top' as const, label: 'Top', icon: faObjectsAlignTop },
			{
				value: 'middle' as const,
				label: 'Middle',
				icon: faObjectsAlignCenterVertical,
			},
			{ value: 'bottom' as const, label: 'Bottom', icon: faObjectsAlignBottom },
		];

		return (
			<Card className="mb-4">
				<div className="p-4">
					<div className="flex items-center mb-4">
						<Icon
							icon={faAlignLeft}
							size="sm"
							className={`mr-2 ${colors.classes.textSecondary}`}
						/>
						<h3
							className={`text-base font-semibold ${colors.classes.textPrimary}`}
						>
							Alignment
						</h3>
					</div>

					{/* Horizontal Alignment */}
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-2`}
					>
						Horizontal Alignment
					</label>
					<div className="flex gap-2 mb-4">
						{horizontalAlignmentOptions.map((option) => (
							<button
								key={option.value}
								onClick={() => onUpdateColumn({ alignment: option.value })}
								className={`
                  flex-1 flex items-center justify-center p-3 rounded-lg border-2 transition-colors
                  ${
										selectedColumn.alignment === option.value
											? 'bg-blue-50 dark:bg-blue-950 border-blue-600 dark:border-blue-500'
											: `${colors.classes.bgPrimary} ${colors.classes.borderDefault} hover:${colors.classes.borderHover}`
									}
                `}
							>
								<Icon
									icon={option.icon}
									size="sm"
									className={`mr-1.5 ${
										selectedColumn.alignment === option.value
											? colors.classes.textInfo
											: colors.classes.textSecondary
									}`}
								/>
								<span
									className={`
                  text-xs
                  ${
										selectedColumn.alignment === option.value
											? 'text-blue-600 dark:text-blue-400 font-semibold'
											: `${colors.classes.textSecondary} font-medium`
									}
                `}
								>
									{option.label}
								</span>
							</button>
						))}
					</div>

					{/* Vertical Alignment */}
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-2`}
					>
						Vertical Alignment
					</label>
					<div className="flex gap-2">
						{verticalAlignmentOptions.map((option) => (
							<button
								key={option.value}
								onClick={() =>
									onUpdateColumn({ verticalAlignment: option.value })
								}
								className={`
                  flex-1 flex items-center justify-center p-3 rounded-lg border-2 transition-colors
                  ${
										(selectedColumn.verticalAlignment || 'top') === option.value
											? 'bg-blue-50 dark:bg-blue-950 border-blue-600 dark:border-blue-500'
											: `${colors.classes.bgPrimary} ${colors.classes.borderDefault} hover:${colors.classes.borderHover}`
									}
                `}
							>
								<Icon
									icon={option.icon}
									size="sm"
									className={`mr-1.5 ${
										(selectedColumn.verticalAlignment || 'top') === option.value
											? colors.classes.textInfo
											: colors.classes.textSecondary
									}`}
								/>
								<span
									className={`
                  text-xs
                  ${
										(selectedColumn.verticalAlignment || 'top') === option.value
											? 'text-blue-600 dark:text-blue-400 font-semibold'
											: `${colors.classes.textSecondary} font-medium`
									}
                `}
								>
									{option.label}
								</span>
							</button>
						))}
					</div>
				</div>
			</Card>
		);
	};

	const renderSortingSection = () => {
		const hasSort =
			selectedColumn.sortFields && selectedColumn.sortFields.length > 0;
		const columnSortId =
			hasSort && selectedColumn.sortFields?.[0]
				? `column-${selectedColumn.id}-${selectedColumn.sortFields[0].direction.toLowerCase()}`
				: null;
		const isDefault = columnSortId && listConfig.defaultSortId === columnSortId;

		return (
			<Card className="mb-4">
				<div className="p-4">
					<div className="flex items-center mb-4">
						<Icon
							icon={faSort}
							size="sm"
							className={`mr-2 ${colors.classes.textSecondary}`}
						/>
						<h3
							className={`text-base font-semibold ${colors.classes.textPrimary}`}
						>
							Header Click Sorting
						</h3>
					</div>

					{/* Radio button for default sort - only show if column has sort fields */}
					{hasSort && (
						<button
							onClick={() => {
								if (columnSortId) {
									onUpdateList({ defaultSortId: columnSortId });
								}
							}}
							className={`flex items-center mb-4 p-3 ${colors.classes.bgPrimary} border ${colors.classes.borderDefault} rounded-lg w-full hover:${colors.classes.bgPrimaryHover} transition-colors`}
						>
							<div
								className={`
                w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center
                ${
									isDefault
										? 'border-blue-600 bg-blue-600'
										: `${colors.classes.borderDefault} ${colors.classes.bgPrimary}`
								}
              `}
							>
								{isDefault && (
									<div className="w-1.5 h-1.5 rounded-full bg-white" />
								)}
							</div>
							<span className={`text-sm ${colors.classes.textPrimary}`}>
								Default Table Sort
							</span>
						</button>
					)}

					<SortFieldsList
						sortFields={selectedColumn.sortFields || []}
						availableFields={availableFieldsFromObjects}
						onUpdate={(sortFields) => onUpdateColumn({ sortFields })}
						showAddButton={true}
					/>
				</div>
			</Card>
		);
	};

	const widthPresets: Array<{
		value: ColumnWidthConfig;
		label: string;
		description: string;
	}> = [
		{
			value: 'flexible',
			label: 'Flexible',
			description: 'Auto-adjusts to content',
		},
		{ value: 'small', label: 'Small', description: '~100px' },
		{ value: 'medium', label: 'Medium', description: '~180px' },
		{ value: 'large', label: 'Large', description: '~280px' },
		{ value: 'xlarge', label: 'X-Large', description: '~400px' },
	];

	const renderWidthSection = (): React.ReactElement => (
		<Card className="mb-4">
			<div className="p-4">
				<div className="flex items-center mb-4">
					<Icon
						icon={faRulerHorizontal}
						size="sm"
						className={`mr-2 ${colors.classes.textSecondary}`}
					/>
					<h3
						className={`text-base font-semibold ${colors.classes.textPrimary}`}
					>
						Column Width
					</h3>
				</div>

				{/* Width Preset */}
				<div className="mb-4">
					<Text className="text-sm font-medium mb-2" color="secondary">
						Width Preset
					</Text>
					<div className="flex flex-wrap gap-2">
						{widthPresets.map((preset) => (
							<button
								key={preset.value}
								onClick={() => {
									onUpdateColumn({ widthConfig: preset.value });
								}}
								className={cn(
									'px-3 py-2 rounded-lg border-2 transition-colors text-xs',
									selectedColumn.widthConfig === preset.value
										? 'bg-blue-50 dark:bg-blue-950 border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 font-semibold'
										: `${colors.classes.bgPrimary} ${colors.classes.borderDefault} ${colors.classes.textSecondary} font-medium hover:${colors.classes.borderHover}`,
								)}
								title={preset.description}
							>
								{preset.label}
							</button>
						))}
					</div>
				</div>

				{/* Custom Width */}
				<div className="mb-4">
					<Text className="text-sm font-medium mb-2" color="secondary">
						Fixed Width (pixels)
					</Text>
					<Input
						type="number"
						value={
							selectedColumn.width != null ? String(selectedColumn.width) : ''
						}
						onChange={(e) => {
							const val = parseInt(e.target.value, 10);
							onUpdateColumn({
								width: !isNaN(val) && val > 0 ? val : undefined,
							});
						}}
						placeholder="e.g., 200"
					/>
					<Text className="text-xs mt-1" color="secondary">
						Override preset with a specific pixel width
					</Text>
				</div>

				{/* Min/Max Width */}
				<div className="grid grid-cols-2 gap-3">
					<div>
						<Text className="text-sm font-medium mb-2" color="secondary">
							Min Width (px)
						</Text>
						<Input
							type="number"
							value={
								selectedColumn.minWidth != null
									? String(selectedColumn.minWidth)
									: ''
							}
							onChange={(e) => {
								const val = parseInt(e.target.value, 10);
								onUpdateColumn({
									minWidth: !isNaN(val) && val > 0 ? val : undefined,
								});
							}}
							placeholder="180"
						/>
					</div>
					<div>
						<Text className="text-sm font-medium mb-2" color="secondary">
							Max Width (px)
						</Text>
						<Input
							type="number"
							value={
								selectedColumn.maxWidth != null
									? String(selectedColumn.maxWidth)
									: ''
							}
							onChange={(e) => {
								const val = parseInt(e.target.value, 10);
								onUpdateColumn({
									maxWidth: !isNaN(val) && val > 0 ? val : undefined,
								});
							}}
							placeholder="400"
						/>
					</div>
				</div>
			</div>
		</Card>
	);

	return (
		<div className="flex-1 overflow-y-auto p-4">
			{renderHeadersSection()}
			{renderAlignmentSection()}
			{renderWidthSection()}
			{renderSortingSection()}
		</div>
	);
}
